import React from "react";
import ReverseTimerClock from "components/ReverseTimerClock";
import FinishButton from "components/promises/FinishButton";
import { FiCheck } from "react-icons/fi";

const PromiseCard = ({
  item,
  isSelected,
  onCardClick,
  showTimers,
  isDisabled,
  hidePrimaryButton,
}) => {
  const predictorName =
    [item.predictor?.firstName, item.predictor?.lastName]
      .filter(Boolean)
      .join(" ") ||
    item.authorName ||
    "Անհայտ";

  const predictorDegree = item.predictor?.degree || item.authorRole || "";
  const avatarUrl =
    item.predictor?.image ||
    item.image ||
    "/defaultuserprofile.webp";

  const hasDistinctImage = !!(item.image && item.image !== item.predictor?.image);

  return (
    <div
      className={`relative w-full rounded-2xl border transition-all duration-200 flex flex-col justify-between p-5 ${
        isSelected
          ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/20 shadow-md"
          : "border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-md"
      }`}
    >
      {/* Selected Indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shadow-sm z-10">
          <FiCheck strokeWidth={3} size={12} />
        </div>
      )}

      {/* Main Clickable Area */}
      <div
        onClick={() => {
          if (!isDisabled && onCardClick) onCardClick();
        }}
        className={`flex-1 flex flex-col justify-between ${
          isDisabled ? "opacity-90" : "cursor-pointer group"
        }`}
      >
        <div>
          {/* Header: Predictor info & Type badge */}
          <div className="flex items-center justify-between gap-3 mb-3.5">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={avatarUrl}
                alt={predictorName}
                onError={(e) => {
                  e.target.src = "/defaultuserprofile.webp";
                }}
                className="w-11 h-11 rounded-full object-cover ring-2 ring-slate-100 shrink-0 bg-slate-50"
              />
              <div className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                  {predictorName}
                </span>
                {predictorDegree && (
                  <span className="block text-xs text-slate-500 truncate">
                    {predictorDegree}
                  </span>
                )}
              </div>
            </div>

            {/* Status / Category Badges */}
            <div className="flex items-center gap-1.5 shrink-0">
              {item.type === "finished" ? (
                item.result ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <FiCheck size={11} strokeWidth={2.5} /> Կատարված է
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                    ✕ Չկատարված
                  </span>
                )
              ) : (
                <span className="inline-flex items-center text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                  Խոստում
                </span>
              )}
            </div>
          </div>

          {/* Content (Title & Description) */}
          <div className="flex gap-4">
            <div className="flex-1 min-w-0">
              {item._highlightResult?.title?.value ? (
                <div
                  className="highlighted text-sm sm:text-base font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-indigo-600 transition-colors"
                  dangerouslySetInnerHTML={{
                    __html: item._highlightResult?.title?.value,
                  }}
                />
              ) : (
                <h4 className="text-sm sm:text-base font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-indigo-600 transition-colors">
                  {item.title}
                </h4>
              )}

              {item.description && (
                <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mt-1.5">
                  {item.description}
                </p>
              )}
            </div>

            {/* Optional distinct thumbnail */}
            {hasDistinctImage && (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden shrink-0 border border-slate-100 bg-slate-50">
                <img
                  src={item.image}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timers & Actions (if showTimers is true) */}
      {showTimers && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 w-full sm:w-auto">
            <ReverseTimerClock
              title="Քվեարկության ավարտ"
              targetTime={item.type === "finished" ? 0 : item.endOfVoting}
            />
            <ReverseTimerClock
              title="Վերջնաժամկետ"
              targetTime={item.type === "finished" ? 0 : item.deadline}
            />
          </div>

          {!hidePrimaryButton && (
            <div className="shrink-0 w-full sm:w-auto">
              <FinishButton promise={item} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromiseCard;
